
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AuthFormState } from "./types";
import { useTranslation } from "react-i18next";

export const useAuthValidation = (formState: AuthFormState) => {
  const { t } = useTranslation();
  
  const checkUsernameExists = async (username: string): Promise<boolean> => {
    try {
      console.log("Checking if username exists:", username);
      
      if (!username || username.length < 3) {
        console.log("Username too short, skipping check");
        return false;
      }
      
      // Perform the database check
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("username")
        .eq("username", username)
        .maybeSingle();
      
      if (error) {
        console.error("Error checking username:", error);
        return false;
      }
      
      const exists = Boolean(profiles);
      console.log("Username exists check result:", exists, "Data:", profiles);
      return exists;
    } catch (error) {
      console.error("Error checking username:", error);
      // Return false on error to allow user to continue
      return false;
    }
  };

  const validateStep = async (mode: "signup" | "login"): Promise<boolean> => {
    if (mode === "login") {
      if (!formState.loginIdentifier || !formState.password) {
        toast({
          title: "Error",
          description: t('auth.enterCredentials', "Please enter your username/email and password"),
          variant: "destructive",
        });
        return false;
      }
      return true;
    }

    // Signup validation
    if (formState.step === 1) {
      if (!formState.name || !formState.email || !formState.username || !formState.password) {
        toast({
          title: "Error",
          description: t('auth.allFieldsRequired', "All fields are required"),
          variant: "destructive",
        });
        return false;
      }

      if (formState.password.length < 6) {
        toast({
          title: "Error",
          description: t('auth.passwordLength', "Password must be at least 6 characters"),
          variant: "destructive",
        });
        return false;
      }

      if (formState.username.length < 3) {
        toast({
          title: "Error",
          description: t('auth.usernameLength', "Username must be at least 3 characters"),
          variant: "destructive",
        });
        return false;
      }

      // Only check username status if it's in a valid state
      if (formState.usernameStatus === "taken") {
        toast({
          title: "Error",
          description: t('auth.usernameTaken', "This username is already taken"),
          variant: "destructive",
        });
        return false;
      }

      // If the check is still in progress, but let user proceed after 3 seconds
      if (formState.usernameStatus === "checking" && formState.usernameCheckStartTime) {
        const checkDuration = Date.now() - formState.usernameCheckStartTime;
        if (checkDuration < 3000) {
          toast({
            title: "Please wait",
            description: t('auth.checkingUsername', "Still checking if username is available..."),
          });
          return false;
        }
        // If check has been running for more than 3 seconds, assume it's okay
        console.log("Username check taking too long, allowing to continue");
      }

      return true;
    }

    if (formState.step === 2) {
      if (!formState.selectedAvatar && formState.avatarTab === "preset") {
        toast({
          title: "Error",
          description: t('auth.selectAvatar', "Please select an avatar"),
          variant: "destructive",
        });
        return false;
      }
      return true;
    }

    return true;
  };

  return {
    validateStep,
    checkUsernameExists,
  };
};
